/**
 * Drag-and-drop upload slot. Handles the register -> PUT -> complete flow per file and
 * reports progress. `meta` says what the files are (kind, criterion for examples).
 *
 * Any file type is accepted. A slot holds at most MAX_FILES_PER_SLOT files (the backend
 * enforces the same number); pass `remaining` so a selection larger than the free room is
 * trimmed before anything is uploaded. Each file must be within the size bounds of the
 * `file_register` schema; files outside them are refused here, before registration, instead
 * of failing at the server.
 *
 * The drop zone always states the limits itself; callers describe only what belongs in it.
 */
import { useRef, useState, type DragEvent } from 'react'
import { uploadFile, type UploadMeta } from '@/api/upload'
import type { FileItem } from '@/lib/types'
import fileRegister from '@schemas/file_register.schema.json'

/** Same number as `MAX_FILES_PER_SLOT` in backend/seldon_tasks/workflow.py. */
export const MAX_FILES_PER_SLOT = 10

/** Per-file size bounds, read from the registration schema the backend validates against. */
export const MIN_FILE_BYTES = fileRegister.properties.size.minimum
export const MAX_FILE_BYTES = fileRegister.properties.size.maximum

const GIB = 1024 ** 3
const MAX_FILE_LABEL = `${MAX_FILE_BYTES / GIB} GiB`

/** Why this file cannot be uploaded, or null when its size is within the bounds. */
export function fileSizeError(file: File): string | null {
  if (file.size < MIN_FILE_BYTES) return 'This file is empty. Choose a file with content.'
  if (file.size > MAX_FILE_BYTES) return `This file is ${(Math.ceil(file.size / GIB * 10) / 10).toFixed(1)} GiB, over the ${MAX_FILE_LABEL} limit. Compress it or split it into smaller files.`
  return null
}

interface Props {
  taskId: string
  disabled?: boolean
  meta: UploadMeta
  label: string
  /** What belongs in this slot. The file types and limits are appended by the drop zone. */
  hint?: string
  /** Which file types to use; defaults to any. */
  types?: string
  /** How many more files the slot can take; defaults to the full slot size. */
  remaining?: number
  onUploaded: (file: FileItem) => void
  onSettled?: () => void
  onBusy?: (busy: boolean) => void
}

/** `flagged`: the file was accepted, but the program checks noted these points (advisory, nothing is blocked). */
interface Progress { id: string; name: string; pct: number; error?: string; flagged?: string[] }

export function FileDrop({ taskId, meta, label, hint, types = 'any file type', remaining = MAX_FILES_PER_SLOT, onUploaded, onSettled, onBusy, disabled = false }: Props) {
  const [over, setOver] = useState(false)
  const [items, setItems] = useState<Progress[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const room = Math.max(0, Math.min(MAX_FILES_PER_SLOT, remaining))

  async function handle(files: FileList | File[]) {
    if (disabled) return
    onBusy?.(true)
    // Refuse out-of-bounds files before anything is registered, so they take no room in the slot.
    // A file chosen again replaces its earlier failed row instead of appearing beside it.
    const incoming = Array.from(files)
    const names = new Set(incoming.map((f) => f.name))
    const list: File[] = []
    const refused: Progress[] = []
    for (const f of incoming) {
      const error = fileSizeError(f)
      if (error) refused.push({ id: crypto.randomUUID(), name: f.name, pct: 0, error })
      else list.push(f)
    }
    setItems((s) => [...s.filter((it) => !(it.error && names.has(it.name))), ...refused])
    if (list.length > room) {
      list.splice(room)
      setNotice(`A slot holds at most ${MAX_FILES_PER_SLOT} files; ${room === 0 ? 'nothing was' : `only the first ${room} ${room === 1 ? 'was' : 'were'}`} added.`)
    } else setNotice(null)
    for (const f of list) {
      const id = crypto.randomUUID()
      setItems((s) => [...s, { id, name: f.name, pct: 0 }])
      const update = (patch: Partial<Progress>) => setItems((s) => s.map((it) => (it.id === id ? { ...it, ...patch } : it)))
      try {
        const rec = await uploadFile(taskId, f, meta, (pct) => update({ pct }))
        const flagged = rec.validation && rec.validation.ok === false ? rec.validation.errors : []
        if (flagged.length) update({ pct: 100, flagged })
        else setItems((s) => s.filter((it) => it.id !== id))
        onUploaded(rec)
      } catch (e) {
        update({ error: (e as Error).message })
      } finally { onSettled?.() }
    }
    onBusy?.(false)
  }

  function onDrop(e: DragEvent) {
    e.preventDefault(); setOver(false)
    if (e.dataTransfer.files.length) void handle(e.dataTransfer.files)
  }

  return (
    <div>
      <div
        className={`slot ${over ? 'over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(!disabled) }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => { if (!disabled) inputRef.current?.click() }}
        role="button" aria-disabled={disabled} tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); inputRef.current?.click() } }}
      >
        <strong>{label}</strong>
        <div className="tiny">{[hint ?? 'Drop files here or choose files', types, `up to ${MAX_FILES_PER_SLOT} files`, `${MAX_FILE_LABEL} each`].join(' · ')}</div>
        <input ref={inputRef} type="file" disabled={disabled} multiple onChange={(e) => { if (e.target.files?.length) void handle(e.target.files); e.target.value = '' }} />
      </div>
      {notice && <p className="muted small">{notice}</p>}
      {items.length > 0 && (
        <ul className="filelist">
          {items.map((it) => (
            <li key={it.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="row spread"><span>{it.name}</span><span className="muted small">{it.error ? 'failed' : it.flagged ? 'uploaded' : `${it.pct}%`}</span></div>
              {!it.error && !it.flagged && <div className="progress"><div style={{ width: `${it.pct}%` }} /></div>}
              {it.error && <div className="field-error">{it.error}</div>}
              {it.flagged && <div className="muted small">Uploaded. Our checks noted the following; nothing is blocked, the developer will see this too:<ul>{it.flagged.slice(0, 5).map((m, i) => <li key={i}>{m}</li>)}</ul></div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
